import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import UrgencyBar from '@/components/feature/UrgencyBar';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import { useNavigate } from 'react-router-dom';

const COMMERCIAL_SERVICES = [
  {
    icon: 'ri-building-4-line',
    title: 'Office Flooring',
    desc: 'Carpet, LVP, and hard surface solutions for professional workspaces. Minimal disruption to business operations.',
  },
  {
    icon: 'ri-store-3-line',
    title: 'Retail Spaces',
    desc: 'High-traffic flooring that looks great and stands up to heavy foot traffic. Fast turnaround for retail schedules.',
  },
  {
    icon: 'ri-hotel-line',
    title: 'Multi-Family & Hospitality',
    desc: 'Bulk flooring installation for apartments, hotels, and condos. Coordinated scheduling for minimal tenant impact.',
  },
  {
    icon: 'ri-community-line',
    title: 'Property Management',
    desc: 'Ongoing partnerships with property managers for unit turns, renovations, and maintenance flooring needs.',
  },
];

const WHY_COMMERCIAL = [
  { icon: 'ri-time-line', title: 'On-Time Delivery', desc: 'We hit deadlines. Your project stays on schedule.' },
  { icon: 'ri-team-line', title: 'Large Crew Capacity', desc: 'Scale up for big jobs. No project too large.' },
  { icon: 'ri-shield-check-line', title: 'Fully Insured', desc: 'General liability and workers comp for peace of mind.' },
  { icon: 'ri-file-list-3-line', title: 'Detailed Quotes', desc: 'Transparent pricing with no hidden costs or surprises.' },
];

const PROJECT_TYPES = [
  'New Construction',
  'Tenant Improvements',
  'Renovations & Remodels',
  'Unit Turns & Make-Ready',
  'Maintenance & Repairs',
  'Retail Build-Outs',
];

export default function CommercialPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <UrgencyBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[500px] md:h-[600px] flex items-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/926faef96b65248ef74def744e9b1ba8.jpg"
              alt="Commercial flooring installation Tampa Bay"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1a17]/90 via-[#1c1a17]/70 to-[#1c1a17]/40" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="max-w-2xl">
              <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">
                VM Construction
              </p>
              <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
                Commercial Flooring & Construction
              </h1>
              <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-xl">
                Professional flooring solutions for offices, retail, multi-family, and hospitality. 
                Tampa Bay&apos;s trusted partner for commercial projects of any scale.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="bg-[#c9a55a] hover:bg-[#b8943f] text-white font-bold text-sm px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Request Commercial Quote
                </button>
                <a
                  href="tel:+19044019383"
                  className="border border-white/30 hover:border-white/60 text-white font-medium text-sm px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-phone-line" />
                  (904) 401-9383
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <section className="bg-[#1c1a17] border-b border-white/10 py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              {[
                { label: 'Licensed & Insured', icon: 'ri-shield-check-line' },
                { label: 'Tampa Bay & Sarasota', icon: 'ri-map-pin-line' },
                { label: 'Large Crew Capacity', icon: 'ri-team-line' },
                { label: 'On-Time Delivery', icon: 'ri-time-line' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${item.icon} text-[#c9a55a] text-lg`} />
                  </div>
                  <span className="text-white/90 text-sm font-medium whitespace-nowrap">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-24 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">
                What We Do
              </p>
              <h2 className="text-4xl md:text-5xl font-black text-[#1c1a17] leading-tight mb-6">
                Commercial Services
              </h2>
              <p className="text-[#6b6560] text-base leading-relaxed max-w-2xl mx-auto">
                From single office suites to multi-building developments, we deliver 
                professional results on time and on budget.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {COMMERCIAL_SERVICES.map((service) => (
                <div
                  key={service.title}
                  className="group flex items-start gap-5 bg-white border border-[#e5e0d8] hover:border-[#c9a55a]/40 rounded-2xl p-8 transition-all duration-300 cursor-default"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-[#f4f0ea] group-hover:bg-[#c9a55a]/10 rounded-xl flex-shrink-0 transition-colors duration-300">
                    <i className={`${service.icon} text-[#c9a55a] text-2xl`} />
                  </div>
                  <div>
                    <h3 className="text-[#1c1a17] font-bold text-lg mb-2">{service.title}</h3>
                    <p className="text-[#6b6560] text-sm leading-relaxed">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Types + Why Us */}
        <section className="bg-[#f4f0ea] py-24 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left — Project Types */}
              <div>
                <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">
                  Project Types
                </p>
                <h2 className="text-3xl md:text-4xl font-black text-[#1c1a17] leading-tight mb-8">
                  We Handle It All
                </h2>
                <div className="space-y-4">
                  {PROJECT_TYPES.map((type) => (
                    <div
                      key={type}
                      className="flex items-center gap-4 bg-white rounded-xl p-4 border border-[#e5e0d8]"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#c9a55a]/10 rounded-lg flex-shrink-0">
                        <i className="ri-check-line text-[#c9a55a] text-lg" />
                      </div>
                      <span className="text-[#1c1a17] font-medium">{type}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — Why Commercial */}
              <div>
                <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">
                  Why VM Construction
                </p>
                <h2 className="text-3xl md:text-4xl font-black text-[#1c1a17] leading-tight mb-8">
                  Built for Business
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {WHY_COMMERCIAL.map((item) => (
                    <div
                      key={item.title}
                      className="bg-white rounded-xl p-6 border border-[#e5e0d8] hover:border-[#c9a55a]/40 transition-colors duration-300"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#c9a55a]/10 rounded-lg mb-4">
                        <i className={`${item.icon} text-[#c9a55a] text-lg`} />
                      </div>
                      <h3 className="text-[#1c1a17] font-bold text-base mb-1">{item.title}</h3>
                      <p className="text-[#6b6560] text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#1c1a17] py-24 px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#c9a55a]/15 rounded-2xl mx-auto mb-8">
              <i className="ri-briefcase-line text-[#c9a55a] text-3xl" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6">
              Ready to Start Your Commercial Project?
            </h2>
            <p className="text-[#a09890] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Let&apos;s discuss your timeline, budget, and requirements. 
              We&apos;ll provide a detailed quote and project plan tailored to your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="bg-[#c9a55a] hover:bg-[#b8943f] text-white font-bold text-sm px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Get Your Commercial Quote
              </button>
              <a
                href="mailto:vmofficeconstruction@gmail.com"
                className="border border-white/30 hover:border-white/60 text-white font-medium text-sm px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-mail-line" />
                Email Us
              </a>
              <Link
                to="/portfolio"
                className="border border-[#c9a55a]/40 hover:border-[#c9a55a] text-[#c9a55a] font-medium text-sm px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-folder-image-line" />
                View Our Work
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
